import { inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  Subject,
  Observable,
  shareReplay,
  debounceTime,
  distinctUntilChanged,
  startWith,
} from 'rxjs';

export interface SearchConfig {
  debounceTime?: number;
  minLength?: number;
  initialValue?: string;
}

/*
 * Dialogs with search should:
 * 1. Implement SearchableDialog interface
 * 2. Inject tabSearch: tabSearchManager(this.tabManager, config)
 * 3. Expose searchString$ and onSearchInput
 * 4. Call tabSearch.setupAutoSwitch(this.tabs) in ngAfterViewInit
 */
export interface SearchableDialog {
  searchString$: Observable<string>;
  onSearchInput(value: string): void;
}

export interface SearchableComponent {
  searchString$: Observable<string>;
}

export function injectSearchManager(config?: SearchConfig) {
  const destroyRef = inject(DestroyRef);

  const debounce = config?.debounceTime ?? 300;
  const minLength = config?.minLength ?? 0;
  const initialValue = config?.initialValue ?? '';

  const searchSubject = new Subject<string>();

  // * BehaviorSubject semantics: every subscriber first receives the current
  // * value (initially `initialValue`), then the debounced edits. Consumers such
  // * as DataObjectListComponent rely on that first emission and skip(1) it - with
  // * a bare Subject the first typed term was swallowed instead.
  const searchString$ = searchSubject.pipe(
    debounceTime(debounce),
    startWith(initialValue),
    distinctUntilChanged(),
    shareReplay(1),
    takeUntilDestroyed(destroyRef),
  );

  return {
    searchString$,
    // * Internal Subject - use for edge cases, prefer onSearchInput
    searchSubject,
    onSearchInput: (value: string) => {
      if (minLength > 0 && value.length > 0 && value.length < minLength) {
        return;
      }
      searchSubject.next(value);
    },
    clear: () => searchSubject.next(''),
    getCurrentValue: () => {
      let currentValue = initialValue;
      searchString$.subscribe((val) => (currentValue = val)).unsubscribe();
      return currentValue;
    },
  };
}
